import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { X, Shield } from 'lucide-react';
import { authApi } from '../services/api';
import { GoogleSignInButton } from './auth/GoogleSignInButton';
import { EmailSignInForm } from './auth/EmailSignInForm';
import { AuthSentPanel } from './auth/AuthSentPanel';

/**
 * Passwordless auth modal: Google OAuth + magic-link email.
 *
 * Two visible states:
 *  - Form (`sent === null`): Google button + email form. The "Sign Up" toggle
 *    just reveals an extra referral-code input below the email.
 *  - Sent (`sent` truthy): "check your inbox" confirmation panel.
 */
const AuthModal = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [referralCode, setReferralCode] = useState(() => {
    try {
      return window.localStorage.getItem('pendingReferralCode') || '';
    } catch (err) {
      console.debug('[auth-modal] localStorage read failed:', err?.message);
      return '';
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null); // {message, expires_in_minutes}
  const { loginWithGoogle } = useAuth();

  // Reset on open so a stale "sent" panel doesn't carry across sessions.
  useEffect(() => {
    if (isOpen) {
      setMode('signin');
      setError('');
      setSent(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await authApi.requestMagicLink(
        email.trim().toLowerCase(),
        mode === 'signup' ? referralCode.trim() || null : null,
      );
      try { window.localStorage.removeItem('pendingReferralCode'); } catch (e) { /* noop */ }
      setSent(res);
    } catch (err) {
      setError(err?.message || 'Could not send sign-in link. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL OR ADD ANY FALLBACKS / REDIRECT URLS —
  // breaking the Google OAuth helper here will cascade across the app.
  const handleGoogleLogin = () => loginWithGoogle();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="auth-modal"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-[#1A1A1A] border border-gray-800 rounded-lg w-full max-w-md mx-4 p-6 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
          data-testid="auth-close-btn"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <Shield className="w-12 h-12 text-[#00FF00]" />
          </div>
          <h2 className="text-2xl font-bold text-white">
            {sent ? 'Check your inbox' : 'Welcome to CyberInsti'}
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            {sent ? 'We just sent you a sign-in link.' : 'Sign in or create your account in seconds.'}
          </p>
        </div>

        {sent ? (
          <AuthSentPanel
            email={email}
            sent={sent}
            onUseDifferentEmail={() => setSent(null)}
          />
        ) : (
          <>
            <GoogleSignInButton onClick={handleGoogleLogin} />

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-800" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-[#1A1A1A] text-gray-500 uppercase tracking-wider">
                  or with email
                </span>
              </div>
            </div>

            {error && (
              <div
                className="bg-red-500/10 border border-red-500/40 text-red-400 px-4 py-3 rounded-lg mb-4 text-sm"
                data-testid="auth-error"
              >
                {error}
              </div>
            )}

            <EmailSignInForm
              mode={mode}
              email={email}
              referralCode={referralCode}
              submitting={submitting}
              onEmailChange={setEmail}
              onReferralCodeChange={setReferralCode}
              onSubmit={handleSubmit}
            />

            <p className="text-center text-sm text-gray-400 mt-6">
              {mode === 'signin' ? (
                <>
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setMode('signup')}
                    data-testid="auth-switch-signup"
                    className="text-[#00FF00] hover:underline font-semibold"
                  >
                    Sign up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setMode('signin')}
                    data-testid="auth-switch-signin"
                    className="text-[#00FF00] hover:underline font-semibold"
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
